import { ReactNode, useState } from 'react';
import { SerializedStyles } from '@emotion/react';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, Button, Chip, Heading, Modal, Text, useModalControl, useTooltip } from '@frontend/design-system';
import { metricInfo, SCREENS } from './const';
import { METRIC_VALUE_ICONS } from './icon-const';
import { SimpleTimelineChart, SimpleTimelineBar, DotWithLabel } from './timeline-chart';
import { MetricData, MetricValue, ScreenBase, TimelineBarProps, TimelineDataItem } from './types';

export const StablePerformanceSection = () => {
  const { t } = useTranslation('calls');
  const heading = t('Stable Performance');

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <div
        css={{
          display: 'grid',
          gap: theme.spacing(1),
          gridTemplateColumns: 'max-content max-content',
          alignItems: 'center',
        }}
      >
        <Icon name='speed' color='success' />
        <Heading level={3}>{heading}</Heading>
      </div>
      <Text size='medium'>{t('Loud and clear – your connection was top-notch!')}</Text>
    </div>
  );
};

export const IssuesDetectedSection = ({ variant }: { variant: 'preview' | 'screen' }) => {
  const { t } = useTranslation('calls');
  const heading = t('Issues Detected');

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <div
        css={{
          display: 'grid',
          gap: theme.spacing(1),
          gridTemplateColumns: 'max-content max-content',
          alignItems: 'center',
        }}
      >
        <Icon name='speed' color='warn' />
        {variant === 'preview' ? (
          <Text size='large' weight='bold'>
            {heading}
          </Text>
        ) : (
          <Heading level={3}>{heading}</Heading>
        )}
      </div>
      <Text size={variant === 'preview' ? 'medium' : 'large'}>
        {t(
          `We've detected issues with your network that may have affected the audio quality of this call. You and your caller may have experienced choppy, distorted, or delayed audio. `
        )}
      </Text>
    </div>
  );
};

export const CallQualityAccordionItem = ({
  itemClassname,
  headerClassname,
  onClick,
  hasIssues = false,
}: {
  itemClassname?: SerializedStyles;
  headerClassname?: SerializedStyles;
  onClick?: () => void;
  hasIssues?: boolean;
}) => {
  const { t } = useTranslation('calls');
  return (
    <Accordion.Item title={t('Network Performance')} value='performance' css={itemClassname}>
      <Accordion.Header css={headerClassname}>
        <div css={{ display: 'grid' }}>
          <Text weight='bold' css={{ fontSize: theme.fontSize(18) }}>
            {t('Network Performance')}
          </Text>
          {hasIssues ? (
            <Chip variant='warn' css={{ width: 'max-content', maxWidth: 'max-content' }}>
              {t('Issues Detected')}
            </Chip>
          ) : (
            <Chip variant='success' css={{ width: 'max-content' }}>
              {t('Stable')}
            </Chip>
          )}
        </div>
      </Accordion.Header>
      <Accordion.Body>
        {hasIssues ? (
          <div css={{ display: 'grid', gap: theme.spacing(2) }}>
            <IssuesDetectedSection variant='preview' />
            <Button variant='tertiary' onClick={onClick} css={{ justifySelf: 'start' }}>
              {t('View Details')}
            </Button>
          </div>
        ) : (
          <StablePerformanceSection />
        )}
        {/* <IssuesDetectedSection variant='preview' /> */}
      </Accordion.Body>
    </Accordion.Item>
  );
};

const deskphoneRecommendations = [
  i18next.t('Restart your phones periodically', { ns: 'calls' }),
  i18next.t('Limit heavy internet use during busy hours', { ns: 'calls' }),
  i18next.t('Ensure that your network equipment (router and/or switch) is set up properly', { ns: 'calls' }),
  i18next.t('Ensure that your network equipment is not overloaded with plugged in devices', { ns: 'calls' }),
];

const softphoneRecommendations = [
  i18next.t('Close unused browser tabs to reduce internet traffic', { ns: 'calls' }),
  i18next.t('Close unused running apps on your computer', { ns: 'calls' }),
  i18next.t('Limit the number of devices using your internet', { ns: 'calls' }),
  i18next.t('Conduct an internet speed test', { ns: 'calls' }),
];

export const RecommendationsSection = () => {
  const { t } = useTranslation('calls');
  const heading = t('Recommendations');

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <Heading level={3}>{heading}</Heading>
      <Text size='large' weight='bold'>
        {t('For Desk Phone')}
      </Text>
      <ul
        css={{
          display: 'grid',
          gap: theme.spacing(1),
          paddingInlineStart: theme.spacing(2),
        }}
      >
        {deskphoneRecommendations.map((recommendation) => (
          <Text as='li' size='medium' key={recommendation}>
            {recommendation}
          </Text>
        ))}
      </ul>
      <Text size='large' weight='bold'>
        {t('For Softphone')}
      </Text>
      <ul
        css={{
          display: 'grid',
          gap: theme.spacing(1),
          paddingInlineStart: theme.spacing(2),
        }}
      >
        {softphoneRecommendations.map((recommendation) => (
          <Text as='li' size='medium' key={recommendation}>
            {recommendation}
          </Text>
        ))}
      </ul>
    </div>
  );
};

const MetricItem = ({
  title,
  values,
  onInfoClick,
}: {
  title: string;
  values: MetricValue[];
  onInfoClick: () => void;
}) => {
  return (
    <div css={{ display: 'grid', gridTemplateColumns: 'subgrid', gridColumn: 'span 2' }}>
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          gridTemplateColumns: 'max-content max-content',
          gap: theme.spacing(1),
        }}
      >
        <Text>{title}</Text>
        <Button
          onClick={onInfoClick}
          css={{ width: 16, height: 16, padding: 0 }}
          size='small'
          iconName='info-small'
          variant='secondary'
        />
      </div>
      <Text
        weight='bold'
        as='div'
        css={{
          display: 'grid',
          gap: theme.spacing(2),
          gridTemplateColumns: 'auto auto',
        }}
      >
        {values.map(({ sentiment, label, direction }, index) => (
          <MetricValueBadge key={`title-${index}`} direction={direction} sentiment={sentiment} label={label} />
        ))}
      </Text>
    </div>
  );
};

const MetricValueBadge = ({ sentiment, label, direction }: MetricValue) => {
  const icon = METRIC_VALUE_ICONS[sentiment];
  return (
    <div
      css={{
        display: 'grid',
        alignItems: 'center',
        gridTemplateColumns: 'max-content max-content',
        gap: theme.spacing(1),
      }}
    >
      {icon}
      <Text
        size='large'
        weight='bold'
        css={{
          display: 'grid',
          gap: theme.spacing(0.5),
          gridTemplateColumns: 'max-content max-content',
          alignItems: 'center',
        }}
      >
        {direction && <Icon name={direction === 'inbound' ? 'trend-down-small' : 'trend-up-small'} />}
        {label}
      </Text>
    </div>
  );
};

export const MetricSection = ({ data, subtitle }: { data: MetricData[]; subtitle: ReactNode }) => {
  const { t } = useTranslation('calls');
  const [modalView, setModalView] = useState<keyof typeof metricInfo>('mosMin');
  const heading = t('Call Metrics');
  const { modalProps, closeModal, openModal } = useModalControl();

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <div>
        <Heading level={3}>{heading}</Heading>
        {typeof subtitle === 'string' ? (
          <Text size='medium' color='light'>
            {subtitle}
          </Text>
        ) : (
          subtitle
        )}
      </div>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'max-content max-content',
          columnGap: theme.spacing(3),
          rowGap: theme.spacing(1),
          paddingInline: theme.spacing(2),
        }}
      >
        {data.map(({ type, values }) => (
          <MetricItem
            key={type}
            title={metricInfo[type].title}
            values={values}
            onInfoClick={() => {
              setModalView(type);
              openModal();
            }}
          />
        ))}
        <Modal {...modalProps}>
          <Modal.Header onClose={closeModal}>{metricInfo[modalView].title}</Modal.Header>
          <Modal.Body>
            {metricInfo[modalView].description.map((text, index) => (
              <Text key={index} size='medium'>
                {text}
              </Text>
            ))}
          </Modal.Body>
        </Modal>
      </div>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'max-content max-content',
          gap: theme.spacing(2),
          paddingInline: theme.spacing(2),
        }}
      >
        <div css={{ display: 'grid', gridTemplateColumns: 'max-content max-content', gap: theme.spacing(1) }}>
          <Icon name='trend-down-small' />
          <Text weight='bold' size='small' color='subdued'>
            {t('Inbound audio stream')}
          </Text>
        </div>
        <div css={{ display: 'grid', gridTemplateColumns: 'max-content max-content', gap: theme.spacing(1) }}>
          <Icon name='trend-up-small' />
          <Text weight='bold' size='small' color='subdued'>
            {t('Outbound audio stream')}
          </Text>
        </div>
      </div>
    </div>
  );
};

export const TimelineSection = ({ setScreen }: { setScreen: ScreenBase['setScreen'] }) => {
  const { t } = useTranslation('calls');
  const heading = t('Call Quality');

  const barTypes = {
    positive: PositiveTimelineBar,
    negative: NegativeTimelineBar,
  } as const;

  const data: TimelineDataItem<typeof barTypes>[] = [
    {
      label: '9AM',
      width: '25%',
      type: 'positive',
      details: [{ title: 'Call Quality', label: <DotWithLabel label='High' color={theme.colors.success20} /> }],
    },
    {
      label: '10AM',
      width: '50%',
      type: 'negative',
      details: [
        { title: 'Call Quality', label: <DotWithLabel label='Low' color={theme.colors.critical30} /> },
        {
          title: 'MOS (avg.)',
          label: (
            <span>
              <Text as='span' size='medium' weight='bold' color='error'>
                3.5{' '}
              </Text>
              <Text as='span' size='medium' weight='bold'>
                / 5
              </Text>
            </span>
          ),
        },
        {
          title: 'Duration',
          label: '1m 30s',
        },
      ],
      onClick: (data) => {
        console.log(data);
        setScreen(SCREENS.CALL_QUALITY_DRILLDOWN);
      },
    },
    {
      label: '11AM',
      width: '25%',
      type: 'positive',
      details: [{ title: 'Call Quality', label: <DotWithLabel label='High' color={theme.colors.success20} /> }],
    },
  ];

  return (
    <div
      css={{
        display: 'grid',
        gap: theme.spacing(2),
        alignItems: 'center',
      }}
    >
      <Heading level={3}>{heading}</Heading>
      <SimpleTimelineChart
        barTypes={barTypes}
        data={data}
        axis={{
          startLabel: '9AM',
          endLabel: '11AM',
        }}
        legends={[
          { label: 'Stable Quality', color: theme.colors.success20 },
          { label: 'Low Quality', color: theme.colors.critical30 },
        ]}
      />
    </div>
  );
};

const PositiveTimelineBar = ({
  label,
  width,
  details,
  onClick,
}: Omit<TimelineBarProps, 'background' | 'outlineColor'>) => {
  return (
    <SimpleTimelineBar
      label={label}
      width={width}
      background={theme.colors.success20}
      details={details}
      onClick={onClick}
    />
  );
};

const NegativeTimelineBar = ({
  label,
  width,
  details,
  onClick,
}: Omit<TimelineBarProps, 'background' | 'outlineColor'>) => {
  const {
    tooltipProps: iconTooltipProps,
    triggerProps: iconTriggerProps,
    Tooltip: IconTooltip,
  } = useTooltip({
    trigger: 'hover',
    theme: 'light',
    showArrow: false,
  });

  return (
    <div
      css={{
        width,
        position: 'relative',
      }}
    >
      <Button
        iconName='alert-small'
        variant='secondary'
        {...iconTriggerProps}
        css={{ position: 'absolute', top: -16, left: -8, width: 16, height: 16, padding: 0 }}
      />
      <SimpleTimelineBar
        label={label}
        width={'auto'}
        background={theme.colors.critical30}
        outlineColor={theme.colors.critical50}
        details={details}
        onClick={onClick}
      />
      <IconTooltip
        {...iconTooltipProps}
        css={{
          borderRadius: theme.borderRadius.large,
          width: 320,
          maxWidth: 320,
          padding: 0,
        }}
      >
        <div
          css={{
            display: 'grid',
            gap: theme.spacing(1),
            padding: theme.spacing(3),
            fontWeight: 'normal',
          }}
        >
          <Text as='span' size='small'>
            {label}
          </Text>
          <div css={{ borderTop: `1px solid ${theme.colors.neutral10}` }} />

          <div
            css={{
              display: 'grid',
              gap: theme.spacing(1),
              gridTemplateColumns: 'max-content max-content',
              alignItems: 'center',
            }}
          >
            <Icon name='alert-small' color='warn' />
            <Text as='span' size='medium' weight='bold'>
              {'Quality Alert Sent'}
            </Text>
          </div>
          <Text size='medium'>
            {'The system sends quality alerts when the Call Metrics drop below the thresholds for stable connections.'}
          </Text>
        </div>
      </IconTooltip>
    </div>
  );
};
